/**
 * The hub's slot renderer — extracted from `DropHubSurface.tsx` against the
 * 400-line ceiling. A factory over the surface's derived data: the surface
 * passes one deps object, the structure calls the returned function per slot.
 * The dispatch logic is verbatim from the surface; see each branch's comment.
 */
import React from "react";
import {SlotRole, SlotFootprint, type SurfaceSlot} from "@/surfaces/slots";
import {SurfaceRailNav} from "@/surfaces/spine/SurfaceRailNav";
import {SurfaceChatPanel} from "@/surfaces/spine/SurfaceChatPanel";
import {SurfaceStage} from "@/surfaces/spine/SurfaceStage";
import {SurfaceStrip} from "@/surfaces/spine/SurfaceStrip";
import {SurfaceRosterRail} from "@/surfaces/spine/SurfaceRosterRail";
import {SurfaceNewsRail} from "@/surfaces/spine/SurfaceNewsRail";
import SurfaceBadgeStash from "@/surfaces/spine/SurfaceBadgeStash";
import {SurfaceContentBlock} from "@/widgets/ContentBlockRenderer";
import {placedBoardId} from "@/surfaces/placedBoard";
import {StageState} from "@/surfaces/stageOccupant";
import {railItems, rosterMembers, newsItems, modeCards, type SpineDrop} from "@/surfaces/spine/dropSpineData";
import {memberKey} from "@/widgets/rosterMember";
import {memberStageOccupant} from "@/pages/drop/sections/memberStageOccupant";
import {sectionIcon, blockIcon} from "@/surfaces/spine/sectionIcons";
import {scrollToSlot} from "./dropHubChrome";
import {DropHubFeaturedCards} from "../DropHubFeaturedCards";

/* Deps are the surface's own locals, passed through unrenamed. */
export function makeRenderDropHubSlot(d: Record<string, any>) {
    const {
        drop, roster, members, sectionSublabels, sectionById, contentBlockById, liveBlockSlots,
        isWheelSection, wheelAccessible, requestWheel, activeSection, setActiveSection,
        resolvedStage, stageOccupant, setStageOccupant, stageIdleOccupant, stageBase,
        sectionCtas, stageFaces, rosterCards, featured, onTitle, filterGameNames,
        filterMode, highlights, copy, trailerThumb, watchMember, myBadges, gameNews,
        renderSectionContent,
    } = d;

    return function renderDropHubSlot(slot: SurfaceSlot): React.ReactNode {
        if (slot.role === SlotRole.NAV) {
            const sectionItems = railItems(drop as SpineDrop, {
                streamingLiveCount: roster ? members.filter(m => m.isLive).length : undefined,
                sublabels: sectionSublabels,
            }).filter(item => {
                const target = sectionById.get(item.id);
                return !(target && isWheelSection(target)) || wheelAccessible;
            });
            /*
             * THE RAIL IS NAVIGATION, AND BLOCKS ARE DESTINATIONS TOO.
             *
             * `railItems` walks SECTIONS only, so an authored band could never
             * reach the rail however its nav control was set. Same gate as the
             * bar: `showInNav`, falling back to the legacy titled-block rule,
             * with `navLabel` naming the entry.
             */
            const blockItems = (liveBlockSlots ?? [])
                .filter((s: SurfaceSlot) => s.footprint !== SlotFootprint.STAGE)
                .flatMap((s: SurfaceSlot) => {
                    const block = contentBlockById.get(s.id);
                    if (!block || block.showInNav === false) return [];
                    const cfg = Object.values(block).find((v): v is { title?: string; sectionTitle?: string } => {
                        if (!v || typeof v !== "object") return false;
                        const o = v as { title?: unknown; sectionTitle?: unknown };
                        return typeof o.title === "string" || typeof o.sectionTitle === "string";
                    });
                    const authored = cfg?.title ?? cfg?.sectionTitle;
                    if (!authored && block.showInNav === undefined) return [];
                    const label = block.navLabel || authored;
                    if (!label) return [];
                    return [{
                        id: `band-${s.id}`,
                        label,
                        icon: blockIcon(block.type, block.navIcon),
                    }];
                });
            const items = [...sectionItems, ...blockItems];
            return (
                <SurfaceRailNav
                    items={items}
                    activeId={activeSection ?? items[0]?.id}
                    onSelect={id => {
                        const target = sectionById.get(id);
                        if (target && isWheelSection(target)) {
                            requestWheel();
                            return;
                        }
                        setActiveSection(id);
                        /*
                         * Selecting is not arriving — the rail marked the entry
                         * and moved nothing. Band entries carry a `band-` prefix
                         * over the slot's own id; sections are the id.
                         */
                        scrollToSlot(id.startsWith("band-") ? id.slice(5) : id);
                    }}
                />
            );
        }

        /*
         * FEATURE IS A ROLE, NOT AN OCCUPANT.
         *
         * This branch used to return the featured cards for
         * ANY feature-role slot, and it sits thirty lines
         * above the content-block lookup — so a board, which
         * is FEATURE/COLUMN in both placement vocabularies,
         * rendered the featured media cards instead and could
         * never reach the column it exists for. The occupancy
         * engine was arbitrating between candidates that all
         * drew the same thing.
         *
         * The featured cards are the column's DEFAULT
         * occupant, which `dropSlots` emits under the id
         * "featured". Anything else claiming the role is a
         * different occupant and resolves below.
         */
        if (slot.role === SlotRole.FEATURE && slot.id === "featured") {
        /* EXPANDED hands the column to the stream's chat (anchor contract). */
            if (resolvedStage.columnIsChat && stageOccupant?.chat) {
                return <SurfaceChatPanel chat={stageOccupant.chat} />;
            }
            return (
                <DropHubFeaturedCards
                    featured={featured}
                    onTitle={onTitle}
                    filterGameNames={filterGameNames}
                    filterMode={filterMode}
                    highlights={highlights}
                    copy={copy}
                    drop={drop}
                    trailerThumb={trailerThumb}
                    onWatchFeatured={watchMember}
                    onWatchTrailer={setStageOccupant}
                />
            );
        }

        if (slot.role === SlotRole.STAGE) {
        /*
         * The Stage's state comes from the SHARED resolver,
         * not from a local ternary.
         *
         * It was `stageOccupant ? "WATCHING" : "IDLE"`,
         * which made EXPANDED unreachable — the state
         * existed in the enum, in `HubSpine.scss` and in
         * the Storybook stories, and no product code could
         * ever produce it. Routing through `resolveStage`
         * means this host asks the same question every
         * other host asks, and gets the same answer.
         */
            /* The roster lives on the STREAMING section; absent, the pile is inert. */
            const rosterSlotId = [...sectionById.entries()]
                .find(([, sec]) => sec?.type === "STREAMING")?.[0];

            return (
                <SurfaceStage
                    state={resolvedStage.state === StageState.EXPANDED
                        ? "EXPANDED"
                        : resolvedStage.state === StageState.WATCH
                            ? "WATCHING"
                            : "IDLE"}
                    occupant={stageOccupant ?? undefined}
                    idleOccupant={stageIdleOccupant}
                    onCloseOccupant={() => setStageOccupant(null)}
                    {...stageBase}
                    actions={sectionCtas.length > 0 ? <>{sectionCtas}</> : stageBase.actions}
                    faces={stageFaces}
                    facesOverflow={Math.max(0, rosterCards.length - stageFaces.length)}
                    facesLabel={roster?.teamName}
                    /*
                     * The pile reads as a control, so it is one — it goes to the
                     * roster's own section. Only when that section exists; a
                     * button that scrolls nowhere is the same lie.
                     */
                    onFacesClick={rosterSlotId
                        ? () => {
                            setActiveSection(rosterSlotId);
                            scrollToSlot(rosterSlotId);
                        }
                        : undefined}
                />
            );
        }

        if (slot.id === "modes") {
            return <SurfaceStrip kicker={copy.modesKicker} cards={modeCards(drop.modes ?? [])} />;
        }

        const contentBlock = contentBlockById.get(slot.id);
        if (contentBlock) {
            return (
                <SurfaceContentBlock
                    block={contentBlock}
                    themeColors={drop.themeColors}
                    brandName={drop.name}
                    surfaceSlug={drop.slug}
                    /* The board this surface places — the earn section's cards are its assigned objectives. */
                    ledgerBoardId={placedBoardId(drop.sections, drop.contentBlocks)}
                    /*
                     * NOT `pointDisplay` — that prop is deliberately
                     * missing `nextLevelPoints` here (see the
                     * `SurfaceTopBar` `member` comment):
                     * `useGetLevelConfigs()` is the real threshold
                     * source, and a hardcoded 1000 is the
                     * false-progress-bar bug this file avoids
                     * elsewhere. A REWARDS block needing real point
                     * progress is a real gap, not one to paper over
                     * with a guessed value.
                     */
                    pointDisplay={undefined}
                />
            );
        }

        const section = sectionById.get(slot.id);
        const label = section?.label ?? section?.title;

        /* The wheel never renders as a band — SurfaceWheel is its whole surface presence. */
        if (section && isWheelSection(section)) {
            return null;
        }

        if (section?.type === "STREAMING") {
            return (
                <SurfaceRosterRail
                    /*
                     * A roster IS a section of the page — it names itself at
                     * display scale like its siblings, not as an eyebrow.
                     */
                    title={label}
                    note={roster?.teamName}
                    /*
                     * Watchable comes from the stage's own answer, so a card
                     * cannot offer a tap the stage would refuse.
                     */
                    members={rosterMembers(members, filterGameNames, filterMode, roster?.rosterFields ?? [])
                        .map(card => ({
                            ...card,
                            watchable: Boolean(memberStageOccupant(
                                members.find(m => memberKey(m) === card.id),
                                highlights,
                            )),
                        }))}
                    /* The roster's own choice — see RosterCardStyle. */
                    cardStyle={(roster as { cardStyle?: string } | undefined)?.cardStyle}
                    onSelect={id => watchMember(members.find(m => memberKey(m) === id))}
                />
            );
        }

        /*
         * The stash. A count beside a name says how many; it
         * cannot say which, or what any of them was for, so a
         * programme that pays in badges needs a room for them.
         * Keyed on the section's own type, like every other
         * occupant — no campaign vocabulary reaches here.
         */
        if (section?.type === "EXCLUSIVES") {
            return <SurfaceBadgeStash badges={myBadges.badges} title={label} />;
        }

        if (section?.type === "NEWS") {
            const items = newsItems(gameNews?.items ?? []);
            return items.length > 0 ? <SurfaceNewsRail kicker={label} items={items} /> : null;
        }

        return section ? renderSectionContent(section) : null;
    };
}
